import React from "react";
import { Link } from "react-router-dom";
import { isLiveStatus } from "@/lib/gameStatus";

export function TeamBadge({ team, size = 40 }) {
  const abbr = team?.abbr || "??";
  const color = team?.color || "#3A4859";
  return (
    <div
      className="rounded-full grid place-items-center font-display shrink-0 tabnums"
      style={{ width: size, height: size, background: "#1E2A3B", border: `2px solid ${color}`, fontSize: size * 0.36 }}
      aria-label={team?.name || abbr}
      title={team?.name || abbr}
    >
      <span style={{ color: "#F7F8FA" }}>{abbr}</span>
    </div>
  );
}

function StatusPill({ status }) {
  if (isLiveStatus(status)) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#D92D20]/15 text-[#ff6b60] text-[11px] font-display tracking-wide">
        <span className="w-1.5 h-1.5 rounded-full bg-[#D92D20] tt-live-dot" /> LIVE
      </span>
    );
  }
  if (status === "final") {
    return <span className="px-2 py-0.5 rounded-md bg-white/8 text-white/60 text-[11px] font-display tracking-wide">FINAL</span>;
  }
  return <span className="px-2 py-0.5 rounded-md bg-[#F4B740]/15 text-[#F4B740] text-[11px] font-display tracking-wide">UPCOMING</span>;
}

function timeLabel(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export function GameCard({ game }) {
  const { home, away, status, league, week } = game;
  const isFinal = status === "final";
  return (
    <Link to={`/game/${game.id}`} data-testid={`game-card-${game.id}`}
      className="block tt-panel rounded-xl p-4 hover:border-[#3A4859] transition-colors tt-rise">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] tracking-widest text-white/40 font-display">{league} · WK {week}</span>
        <StatusPill status={status} />
      </div>
      <div className="space-y-2">
        {[away, home].map((t, i) => (
          <div key={i} className="flex items-center gap-3">
            <TeamBadge team={t} size={34} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{t.name}</div>
              <div className="text-[11px] text-white/40 tabnums">{t.record ? `${t.record.w}-${t.record.l}` : ""}</div>
            </div>
            <div className="font-display text-2xl text-white tabnums">{t.score != null ? t.score : ""}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-[#2A3544] flex items-center justify-between text-[11px] text-white/45">
        <span>{game.venue || ""}</span>
        <span className="tabnums">{isFinal ? "Final" : timeLabel(game.start_time)}</span>
      </div>
    </Link>
  );
}

// Verified player name. Renders a link to the player page ONLY when a stable
// internal id exists; otherwise plain text (no dead links, no fabricated ids).
export function PlayerName({ player, id, name, className = "", testid }) {
  const pid = id || player?.id || player?.player_id || null;
  const label = name || player?.name || player?.display_name
    || `${player?.first_name || ""} ${player?.last_name || ""}`.trim() || "—";
  if (!pid) return <span className={className} data-testid={testid}>{label}</span>;
  return (
    <Link to={`/player/${encodeURIComponent(pid)}`} className={`${className} hover:text-[#4ea152] transition-colors`} data-testid={testid}>
      {label}
    </Link>
  );
}


export function PageHeader({ title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl text-white leading-none">{title}</h1>
        {subtitle && <p className="text-white/50 text-sm mt-2">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function Empty({ title, detail, icon: Icon }) {
  return (
    <div className="tt-panel rounded-xl p-10 text-center">
      {Icon && <Icon className="mx-auto mb-3 text-white/30" size={28} />}
      <p className="font-display text-xl text-white">{title}</p>
      {detail && <p className="text-white/50 text-sm mt-1">{detail}</p>}
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse rounded-lg bg-white/5 ${className}`} />;
}
